import {
  useWSSnackbar,
  WSActions,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EngagementStatus,
  PayeeEngagementType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesEngagements,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { useWSMutation } from "@ws-react-query";
import { payeeEngagementsApi } from "@api";
import { selectorCounterpartyDisplayName } from "../../../../shared/selectors/selectorCounterpartyDisplayName";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";
import { FormPartialPayeeSearchField } from "../../../../shared/components/PayeeSearchField";
import {
  FormPartialPayeeQboMapping,
  PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES
} from "../../../Integrations/components/FormPartialPayeeQboMapping";
import { useAssignEngagementsBulkAction } from "../../hooks/useAssignEngagementsBulkAction";

export type CreateEngagementModalProps = {
  onClose(result: boolean): void;
  payeeId?: string;
  engagementId?: string;
};

type FormData = {
  payeeId: string;
  engagementIds: string[];
  qboVendorId: string;
  qboExpenseAccountId: string;
};

/**
 * Assign one engagement to a payee. v1 `POST /payee/{id}/engagement` accepted the
 * QuickBooks mapping inline via `payerOwnedData.integration`; v3 splits it in two —
 * `createPayeeEngagement` carries only the assignment (and requires `engagementType`,
 * which v1 never sent and we read off the selected `Engagement`), then the mapping is
 * a follow-up `updatePayeeEngagement` with the flattened `quickbooks*` fields.
 */
const useMutationAssignPayeeEngagement = () =>
  useWSMutation(
    async (request: {
      payeeId: string;
      engagementId: string;
      engagementType: PayeeEngagementType;
      xWingspanAccount?: string;
      quickbooksVendorId?: string;
      quickbooksExpenseAccountId?: string;
    }) => {
      const payeeEngagement = await payeeEngagementsApi.createPayeeEngagement({
        payeeId: request.payeeId,
        payeeEngagementCreate: {
          engagementId: request.engagementId,
          engagementType: request.engagementType
        },
        xWingspanAccount: request.xWingspanAccount
      });

      if (request.quickbooksVendorId || request.quickbooksExpenseAccountId) {
        await payeeEngagementsApi.updatePayeeEngagement({
          payeeId: request.payeeId,
          payeeEngagementId: payeeEngagement.id as string,
          payeeEngagementUpdate: {
            quickbooksVendorId: request.quickbooksVendorId,
            quickbooksExpenseAccountId: request.quickbooksExpenseAccountId
          },
          xWingspanAccount: request.xWingspanAccount
        });
      }

      return payeeEngagement;
    },
    {
      dependencies: [
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase
      ]
    }
  );

export const CreateEngagementModal: React.FC<CreateEngagementModalProps> = ({
  payeeId,
  engagementId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const xWingspanAccount = useXWingspanPayerId();

  const queryEngagementsTypes = queriesEngagements.listEngagements.useQueryAll(
    {
      xWingspanAccount,
      filter: { status: { anyOf: [EngagementStatus.ACTIVE] } }
    },
    { enabled: !!payeeId }
  );

  const queryPayeeEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll(
      { payeeId: payeeId!, xWingspanAccount },
      { enabled: !!payeeId }
    );

  // Direct `getPayee`, not the `searchPayeeRows` projection. The search index is
  // currently unstable, and the only thing the row fed here is the contractor
  // name shown on the prefilled (single-option) contractor field below — not
  // worth a dependency on it. This costs no extra request either: the contractor
  // details screen that opens this modal already reads `getPayee` with the same
  // params, so react-query serves it from cache.
  //
  // Trade: the row carries the server-resolved `profile.resolvedName`, the
  // entity does not, so `selectorCounterpartyDisplayName` falls back through
  // displayName -> company DBA -> email -> id.
  const queryPayee = queriesPayees.getPayee.useQuery(
    { payeeId: payeeId!, xWingspanAccount },
    { enabled: !!payeeId }
  );

  const queryEngagement = queriesEngagements.getEngagement.useQuery(
    { engagementId: engagementId!, xWingspanAccount },
    { enabled: !!engagementId }
  );

  const [createEngagement, createEngagementMeta] =
    useMutationAssignPayeeEngagement();

  const runAssignEngagementsBulkAction = useAssignEngagementsBulkAction();

  const engagementsOptions = useMemo(
    () =>
      queryEngagementsTypes.flatData
        ?.filter(
          engagement =>
            !engagement.isDefault &&
            !queryPayeeEngagements.flatData?.some(
              eng => eng.engagementId === engagement.id
            )
        )
        .map(engagement => ({
          label: engagement.name ?? "",
          value: engagement.id ?? ""
        })) || [],
    [queryEngagementsTypes.flatData, queryPayeeEngagements.flatData]
  );

  const payeeName = queryPayee.data
    ? selectorCounterpartyDisplayName(queryPayee.data)
    : "";

  const handleSubmit = useCallback(
    async (data: FormData) => {
      const engagementIds = data.engagementIds || [];

      if (engagementIds.length === 1) {
        // v3 `createPayeeEngagement` requires `engagementType`; read it off the
        // selected engagement template (v1 inferred it server-side).
        const selectedEngagement = queryEngagementsTypes.flatData?.find(
          engagement => engagement.id === engagementIds[0]
        );

        await createEngagement(
          {
            engagementId: engagementIds[0],
            payeeId: data.payeeId,
            engagementType:
              selectedEngagement?.engagementType ??
              PayeeEngagementType.CONTRACTOR,
            xWingspanAccount,
            // `FormPartialPayeeQboMapping` renders nothing unless the account
            // has a usable QuickBooks connection, so an unmapped or
            // QBO-less submit leaves these at their empty default.
            quickbooksVendorId: data.qboVendorId || undefined,
            quickbooksExpenseAccountId: data.qboExpenseAccountId || undefined
          },
          { throwOnError: true }
        );

        onClose(true);
        openSnackbar({
          message: "Assignment added successfully",
          type: "success"
        });
      } else {
        const result = await runAssignEngagementsBulkAction(
          engagementIds.map(id => ({
            engagementId: id,
            name: engagementsOptions.find(o => o.value === id)?.label,
            payeeId: data.payeeId
          }))
        );
        if (!result) return;

        onClose(result);
      }
    },
    [
      createEngagement,
      engagementsOptions,
      onClose,
      openSnackbar,
      queryEngagementsTypes.flatData,
      runAssignEngagementsBulkAction,
      xWingspanAccount
    ]
  );

  const validationSchema = Yup.object().shape({
    engagementIds: Yup.array()
      .of(Yup.string())
      .min(1, "At least one engagement is required")
      .required("At least one engagement is required"),
    payeeId: Yup.string().required("Contractor is required!")
  });

  return (
    <WSElement
      shimmer={
        // `isInitialLoading`, not `isLoading`: every read here is conditionally
        // enabled, and react-query v4 parks a disabled query in
        // `status: "loading"` forever — `isLoading` would shimmer the modal
        // indefinitely on whichever branch is switched off.
        payeeId
          ? queryPayee.isInitialLoading ||
            queryEngagementsTypes.isInitialLoading ||
            queryPayeeEngagements.isInitialLoading
          : queryEngagement.isInitialLoading
      }
    >
      <WSForm<FormData>
        defaultValues={{
          ...PAYEE_QBO_MAPPING_FORM_DEFAULT_VALUES,
          engagementIds: engagementId ? [engagementId] : [],
          payeeId: payeeId || ""
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <WSList gap="XL">
          {payeeId ? (
            <WSForm.Field
              name="payeeId"
              label="Contractor"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [{ value: payeeId || "", label: payeeName }]
              }}
            />
          ) : (
            <FormPartialPayeeSearchField
              name="payeeId"
              engagementId={engagementId!}
            />
          )}
          {engagementId ? (
            <WSForm.Field
              name="engagementIds"
              label="Engagement"
              component={WSSelect}
              componentProps={{
                disabled: true,
                mode: "multiple",
                options: [
                  {
                    label: queryEngagement.data?.name || "Current engagement",
                    value: engagementId
                  }
                ]
              }}
            />
          ) : engagementsOptions.length > 0 ? (
            <WSForm.Field
              name="engagementIds"
              label="Engagements"
              component={WSSelect}
              componentProps={{
                mode: "multiple",
                options: engagementsOptions,
                placeholder: "Select engagements",
                required: true
              }}
            />
          ) : (
            <WSText.ParagraphSm align="center">
              No engagements available
            </WSText.ParagraphSm>
          )}

          <WSForm.Value name="engagementIds">
            {(engagementIds: string[] | null) => (
              <FormPartialPayeeQboMapping
                disabled={(engagementIds || [])?.length > 1}
              />
            )}
          </WSForm.Value>

          <WSInfoBox title="About assignments">
            Assignments appear on the contractor's profile. They must complete
            any assignment-specific requirements to receive payments. You can
            archive or restore assignments at any time.
          </WSInfoBox>

          <ErrorAlert context="Engagement" error={createEngagementMeta.error} />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Primary",
                label: "Add assignments",
                loading: createEngagementMeta.isLoading,
                type: "submit"
              },
              {
                kind: "Secondary",
                label: "Cancel",
                onClick() {
                  onClose(false);
                },
                type: "button"
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSElement>
  );
};
